import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetDemoBrands,
  useSetDemoBrand,
  useInvalidateUseGetDemoBrands,
  useInvalidateUseGetAgencyInformationForUser,
  type GetDemoBrandsQueryKey,
  type DemoBrandsResponse,
  type DemoBrandResponse,
} from '@urbanx/agx.agencies.client';
import {
  AgxPortal,
  AgxSpacer,
  AgxButton,
  AgxDropdown,
  AgxTypography,
  Icon_CloseOutline,
  useToast,
} from '@urbanx/agx.ui-components2';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { demoBrandPreviewChanged } from 'features/agencies/agenciesReducer';
import { DemoBrandEditor } from './DemoBrandEditor';
import * as styles from './DemoBrands.css';

const queryKey: GetDemoBrandsQueryKey = ['getDemoBrands'];

function DemoBrandsSelection({ data, onClose }: { data: DemoBrandsResponse; onClose: () => void }) {
  const [selectedPresetId, setSelectedPresetId] = useState(data.activePresetId ?? '');
  const [editor, setEditor] = useState<DemoBrandResponse | 'new' | undefined>(undefined);
  const selected = data.presets.find((preset) => preset.id === selectedPresetId);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const invalidateAgency = useInvalidateUseGetAgencyInformationForUser();
  const { setDemoBrand, setDemoBrandPending } = useSetDemoBrand();

  useEffect(() => {
    if (editor) return;
    const branding = selected?.branding ?? data.originalBranding;
    dispatch(
      demoBrandPreviewChanged({
        branding: { ...branding, agencyName: selected?.agencyName ?? data.originalAgencyName },
        logoUrl: branding.logo?.publicPath,
      })
    );
  }, [editor, selected, data.originalBranding, data.originalAgencyName, dispatch]);

  const applyOverride = async () => {
    if (setDemoBrandPending) return;
    try {
      const updated = await setDemoBrand({
        agencyETag: data.agencyETag,
        presetId: selectedPresetId || undefined,
      });
      if (!updated) throw new Error('The agency brand was not returned after applying.');
      queryClient.setQueryData(queryKey, updated);
      await invalidateAgency();
      pushToast({
        type: 'success',
        message: selectedPresetId
          ? 'Demo brand applied to the agency.'
          : 'Original agency brand restored.',
      });
      onClose();
    } catch (error) {
      pushToast({
        type: 'error',
        error,
        fallbackMessage: 'Could not apply the agency brand.',
      });
    }
  };

  if (editor)
    return (
      <DemoBrandEditor
        data={data}
        preset={editor === 'new' ? undefined : editor}
        onBack={() => setEditor(undefined)}
        onSaved={(saved) => {
          queryClient.setQueryData<DemoBrandsResponse>(
            queryKey,
            (current) =>
              current && {
                ...current,
                presets: [...current.presets.filter((preset) => preset.id !== saved.id), saved],
              }
          );
          setSelectedPresetId(saved.id);
          setEditor(undefined);
        }}
        onDeleted={(updated) => {
          queryClient.setQueryData(queryKey, updated);
          setSelectedPresetId('');
          setEditor(undefined);
        }}
      />
    );

  return (
    <div className={styles.form}>
      <div className={styles.selectorRow}>
        <AgxDropdown
          id="demo-brands-presets"
          label="Active override"
          hideOptionalText
          searchable
          selected={selectedPresetId}
          options={[
            { value: '', label: 'None (Agency Default Brand)' },
            ...data.presets.map((preset) => ({ value: preset.id, label: preset.label })),
          ]}
          disabled={setDemoBrandPending}
          onChange={setSelectedPresetId}
        />
        <AgxButton
          id="demo-brands-edit"
          text="Edit"
          variant="hollow"
          size="medium"
          disabled={setDemoBrandPending || !selected}
          onClick={() => setEditor(selected)}
        />
        <AgxButton
          id="demo-brands-create"
          text="Create"
          variant="hollow"
          size="medium"
          disabled={setDemoBrandPending}
          onClick={() => setEditor('new')}
        />
      </div>
      <AgxSpacer direction="horizontal" gap="spacing-sm" wrap>
        <AgxButton
          id="demo-brands-apply"
          text="Apply Selection"
          variant="primary"
          size="medium"
          disabled={setDemoBrandPending}
          onClick={() => void applyOverride()}
        />
      </AgxSpacer>
    </div>
  );
}

export default function DemoBrandsPanel({ onClose }: { onClose: () => void }) {
  const { data, isPending, isError } = useGetDemoBrands();
  const invalidateBrands = useInvalidateUseGetDemoBrands();
  const [refreshing, setRefreshing] = useState(true);
  const refreshBrands = useEffectEvent(() => invalidateBrands());
  const dispatch = useAppDispatch();
  const panel = useRef<HTMLElement>(null);

  // Queries never expire in the portal. Every opening must load fresh presets and ETags.
  useEffect(() => {
    let mounted = true;
    void refreshBrands().then(() => {
      if (mounted) setRefreshing(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const previousFocus = document.activeElement;
    panel.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('keydown', escape);
      dispatch(demoBrandPreviewChanged(undefined));
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [dispatch, onClose]);

  return (
    <AgxPortal>
      <section
        ref={panel}
        className={styles.panel}
        role="dialog"
        aria-label="Demo Brands"
        tabIndex={-1}
      >
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <div className={styles.heading}>
            <AgxTypography
              id="demo-brands-title"
              type="body_ui_strong"
              size="medium"
              role="heading"
              aria-level={2}
            >
              Demo Brands
            </AgxTypography>
          </div>
          <button
            id="demo-brands-close"
            className={styles.closeButton}
            type="button"
            aria-label="Close Demo Brands"
            onClick={onClose}
          >
            <Icon_CloseOutline />
          </button>
        </AgxSpacer>
        {(isPending || refreshing) && (
          <AgxTypography type="body_ui" size="small" role="status">
            Loading brands…
          </AgxTypography>
        )}
        {!refreshing && isError && (
          <AgxTypography type="form_error" size="small" role="alert">
            Could not load demo brands. Check that your account has permission to use Demo Brands
            and try again.
          </AgxTypography>
        )}
        {!refreshing && !isError && data && <DemoBrandsSelection data={data} onClose={onClose} />}
      </section>
    </AgxPortal>
  );
}
